import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, animate } from "framer-motion";
import { Users, Luggage, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { FLEET, BOOKING_URL } from "@/data/site";

const CARD_STEP = 384; // card width + gap for arrow navigation

export const FleetCarousel = ({ dark = false }) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [maxDrag, setMaxDrag] = useState(0);
  const x = useMotionValue(0);

  useEffect(() => {
    const update = () => {
      if (containerRef.current && trackRef.current) {
        const max = Math.max(0, trackRef.current.scrollWidth - containerRef.current.offsetWidth);
        setMaxDrag(max);
        if (x.get() < -max) animate(x, -max, { duration: 0.3 });
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [x]);

  const scrollBy = (dir) => {
    const target = Math.max(-maxDrag, Math.min(0, x.get() - dir * CARD_STEP));
    animate(x, target, { type: "spring", stiffness: 260, damping: 36 });
  };

  const cardBg = dark
    ? "bg-[#141414] border-white/5 hover:border-brand/40"
    : "bg-white border-[#EAEBEA] hover:border-brand/40 hover:shadow-[0_20px_45px_-20px_rgba(0,0,0,0.25)]";
  const titleColor = dark ? "text-white" : "text-ink";
  const descColor = dark ? "text-white/50" : "text-ink/60";
  const metaColor = dark ? "text-white/50" : "text-ink/60";

  return (
    <div className="relative">
      <div ref={containerRef} className="overflow-hidden cursor-grab active:cursor-grabbing">
        <motion.div
          ref={trackRef}
          drag="x"
          dragConstraints={{ left: -maxDrag, right: 0 }}
          dragElastic={0.06}
          style={{ x }}
          className="flex gap-6 w-max pb-2"
          data-testid="fleet-carousel-track"
        >
          {FLEET.map((f, i) => (
            <div
              key={f.name}
              data-testid={`fleet-card-${i}`}
              className={`group relative w-[300px] sm:w-[340px] md:w-[360px] shrink-0 rounded-2xl overflow-hidden border transition-all duration-300 ${cardBg}`}
            >
              <Link to={`/fleet/${f.slug}`} className="block h-52 overflow-hidden" data-testid={`fleet-card-image-${f.slug}`}>
                <img
                  src={f.image}
                  alt={f.name}
                  draggable="false"
                  loading="lazy"
                  className="h-full w-full object-cover select-none pointer-events-none group-hover:scale-105 transition-transform duration-[600ms]"
                />
              </Link>
              <div className="p-6">
                {f.tier && <span className="text-[11px] uppercase tracking-widest text-brand font-bold">{f.tier}</span>}
                <Link to={`/fleet/${f.slug}`} data-testid={`fleet-card-title-${f.slug}`}>
                  <h3 className={`font-heading text-xl font-bold mt-1 mb-2 hover:text-brand transition-colors ${titleColor}`}>{f.name}</h3>
                </Link>
                <p className={`text-sm leading-relaxed mb-4 line-clamp-3 ${descColor}`}>{f.desc}</p>
                <div className={`flex gap-5 text-sm mb-5 ${metaColor}`}>
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-brand" /> {f.seats}</span>
                  <span className="flex items-center gap-1.5"><Luggage className="h-4 w-4 text-brand" /> {f.luggage}</span>
                </div>
                <div className="flex items-center gap-4">
                  <Link
                    to={`/fleet/${f.slug}`}
                    data-testid={`fleet-details-${f.slug}`}
                    className={`inline-flex items-center gap-1.5 font-semibold text-sm hover:gap-2 transition-all ${dark ? "text-white/70 hover:text-white" : "text-ink/70 hover:text-ink"}`}
                  >
                    Details <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                    href={BOOKING_URL}
                    data-testid={`fleet-book-${i}`}
                    className="inline-flex items-center gap-1.5 text-brand font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Book <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button
          type="button"
          data-testid="fleet-prev-btn"
          onClick={() => scrollBy(-1)}
          aria-label="Previous vehicles"
          className={`grid place-items-center h-11 w-11 rounded-full border transition-colors ${dark ? "border-white/20 text-white hover:bg-white/10" : "border-ink/15 text-ink hover:bg-ink hover:text-white"}`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          data-testid="fleet-next-btn"
          onClick={() => scrollBy(1)}
          aria-label="Next vehicles"
          className={`grid place-items-center h-11 w-11 rounded-full border transition-colors ${dark ? "border-white/20 text-white hover:bg-white/10" : "border-ink/15 text-ink hover:bg-ink hover:text-white"}`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <span className={`text-xs ml-2 ${dark ? "text-white/40" : "text-ink/40"}`}>Drag or swipe to explore</span>
      </div>
    </div>
  );
};
